import EventDispatcher from "../../../../../shared/lib/html5-software-library/src/ranj/util/EventDispatcher";
import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import ProgressValue from "../../../../../shared/src/ranj/equalture/platform/ProgressValue";
import LevelEndType from "../data/LevelEndType";
import GameConfig from "./GameConfig";
import GameStatus from "./GameStatus";
import PuzzleModel from "./PuzzleModel";
import TrackingController from "./TrackingController";

export default class GameModel extends EventDispatcher {

    /**
     * @param {GameConfig} gameConfig 
     * @param {TrackingController} trackingController 
     */
    constructor(dispatcher, gameConfig, trackingController) {
        super();
        this._contextDispatcher = dispatcher;
        this._config = gameConfig;
        this._trackingController = trackingController;
    }

    dispatchPlatformProgressEvent(progressValue) {
        this._trackingController.trackProgress(progressValue, this.elapsedTime);
    }

    initialize() {
        this.reset();
    }

    reset() {
        this._puzzleIndex = -1;
        this._elapsedTime = 0;
        this.score = 0;
        this.mistakeCount = 0;

        const debugTime = parseInt(getURLParameter("time"));
        this._availableTime = (!isNaN(debugTime) ? debugTime : this._config.time) * 1000;
    }

    set restoredFeedbackPending(bool) {
        this._restoredFeedbackPending = bool;
    }
    get restoredFeedbackPending() {
        return this._restoredFeedbackPending;
    }

    start() {
        this.dispatchPlatformProgressEvent(ProgressValue.STARTED);
        this.status = GameStatus.RUNNING;
    }

    end() {
        this.status = GameStatus.END;
        this.dispatchPlatformProgressEvent(ProgressValue.COMPLETED);
    }

    timeout() {
        this._trackingController.trackLevelEnd(this.puzzleIndex, LevelEndType.TIMEOUT, this.puzzle.dragDistance, this.elapsedTime);
        this.status = GameStatus.TIMEOUT;
        this.dispatchPlatformProgressEvent(ProgressValue.COMPLETED);
    }

    step(dt) {
        if (this.status != GameStatus.RUNNING) {
            return;
        }
        this.elapsedTime += dt;
        if (this.remainingTime <= 0) {
            this.timeout();
        }
    }

    set elapsedTime(value) {
        this._elapsedTime = value;
        this.dispatchEvent(GameModel.ELAPSED_TIME, this._elapsedTime);
    }
    get elapsedTime() {
        return this._elapsedTime;
    }
    get remainingTime() {
        return this._availableTime - this._elapsedTime;
    }

    /**
     * @type {Number} 
     */
    get score() {
        return this._score;
    }
    set score(value) {
        this._score = value;
        this.dispatchEvent(GameModel.SCORE, value);
    }

    /**
     * @type {Number} 
     */
    get mistakeCount() {
        return this._mistakeCount;
    }
    set mistakeCount(value) {
        this._mistakeCount = value;
        this.dispatchEvent(GameModel.MISTAKES, value);
    }

    set status(value) {
        this._status = value;
        this.dispatchEvent(GameModel.STATUS, value);
    }
    get status() {
        return this._status;
    }


    applySavedState(obj) {
        //restore puzzle
        this.elapsedTime = obj.elapsed;
        this.dispatchPlatformProgressEvent(ProgressValue.STARTED);
        this._restoredFeedbackPending = true;
        this.initPuzzle(obj.index, obj.puzzle);
        //restore scores
        this.score = obj.score;
        this.mistakeCount = obj.mistakeCount;
        this.status = GameStatus.RUNNING;
    }

    hasNextPuzzle() {
        return this.puzzleIndex + 1 < this.puzzleCount;
    }

    doNextPuzzle() {
        if (this.hasNextPuzzle()) {
            this.initPuzzle(this._puzzleIndex + 1);
            this._trackingController.trackLevelStart(this.puzzleIndex, this.elapsedTime);
        } else {
            this.end();
        }
    }

    initTutorialPuzzle() {
        this._puzzleModel = this._createPuzzleModel(this._config.getValue("tutorial.puzzle"));
        this.dispatchEvent(GameModel.PUZZLE, this._puzzleModel);
    }

    /**
     * 
     * @param {Number} index 
     * @param {Object} puzzleState - generic object containing savegame data for puzzle
     */
    initPuzzle(index, puzzleState = null) {
        this._destroyPuzzle();
        this._puzzleIndex = index;
        this._puzzleModel = this._createPuzzleModel(this._config.getPuzzle(index));
        this._addPuzzleEventListeners();
        if (puzzleState) {
            this._puzzleModel.setFromObject(puzzleState);
        }
        this.dispatchEvent(GameModel.PUZZLE, this._puzzleModel);
    }

    _createPuzzleModel(puzzleCfg) {
        return new PuzzleModel(puzzleCfg.location, puzzleCfg.target, puzzleCfg.items);
    }

    _destroyPuzzle() {
        if (this._puzzleModel) {
            this._puzzleModel.removeEventListener(PuzzleModel.MOVE, this._handlePuzzleMove);
            this._puzzleModel = null;
        }
    }

    _addPuzzleEventListeners() {
        this._puzzleModel.addEventListener(PuzzleModel.MOVE, this._handlePuzzleMove);
    }

    selectItem(slotIndex) {
        const itemIndex = this.puzzle.getSlot(slotIndex);
        const correct = itemIndex == this.puzzle.target;
        if (correct) {
            this.puzzle.setSlot(slotIndex, null);//empty slot
            this.score++;
            this.dispatchEvent(GameModel.MATCH, slotIndex);
        } else {
            this.mistakeCount++;
            this.dispatchEvent(GameModel.MISMATCH, slotIndex);
        }
        this._trackingController.trackItemSelection(this.puzzleIndex, correct, this.elapsedTime);
    }


    submitPuzzle() {
        this._trackingController.trackLevelEnd(this.puzzleIndex, LevelEndType.USER, this.puzzle.dragDistance, this.elapsedTime);
        this.dispatchEvent(GameModel.SUBMIT);
    }

    get puzzle() {
        return this._puzzleModel;
    }

    get puzzleIndex() {
        return this._puzzleIndex;
    }

    get puzzleCount() {
        return this._config.puzzleCount;
    }

};


GameModel.STATUS = "GameModel.STATUS";
GameModel.PUZZLE = "GameModel.PUZZLE";
GameModel.SUBMIT = "GameModel.SUBMIT";
GameModel.SCORE = "GameModel.SCORE";
GameModel.MISTAKES = "GameModel.MISTAKES";
GameModel.ELAPSED_TIME = "GameModel.TIME";


GameModel.MATCH = "GameModel.MATCH";
GameModel.MISMATCH = "GameModel.MISMATCH";